import { motion } from "framer-motion";
import { useState } from "react";
import EditorialMarquee from "../components/EditorialMarquee";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TEAM = [
  {
    name: "Megha Bhardwaj",
    role: "Co-Founder · Design Head",
    image: "/team/megha.jpg",
    bio: "Megha Bhardwaj is the creative force behind the firm's design philosophy, bringing a refined aesthetic and a deep understanding of spatial planning to every project. With over 17 years of experience in the field, and expertise in residential, commercial, and hospitality interiors, she leads the design process from concept development to final execution, ensuring each space is functional, timeless, and tailored to the client's vision. Her passion for design, attention to detail, and commitment to quality have been instrumental in shaping the company's reputation for delivering exceptional spaces.",
  },
  {
    name: "Anupam Gulati",
    role: "Co-Founder · Business Head",
    image: "/team/anupam.jpg",
    bio: "Anupam Gulati brings a strong financial and strategic foundation to the company. He has over 17 years of experience in the banking and financial services industry before co-founding the firm. His expertise spans business strategy, financial planning, client relationships, and organizational growth. Anupam leads the company's business development, strategic partnerships, and finance functions, driving sustainable expansion while maintaining operational excellence. His vision is to build a globally recognized design-led brand by combining world-class design, quality craftsmanship, and international collaborations — particularly in premium furniture and interior solutions for the Indian and GCC markets.",
  },
];

export default function Studio() {
  const [selected, setSelected] = useState(null);
  return (
    <main className="bg-bone" data-testid="studio-page">
      <section className="pt-40 md:pt-56 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-muteInk mb-6">— The Studio</p>
          <h1 className="font-serif text-[12vw] md:text-[8vw] leading-[0.9] tracking-tight text-ink">
            A decade of<br /><span className="italic text-clay">quiet obsession.</span>
          </h1>
          <div className="grid grid-cols-12 gap-6 mt-16 md:mt-24">
            <div className="col-span-12 md:col-span-5">
              <div className="clip-frame w-full h-[400px] md:h-[600px]">
                <img
                  src="/studio/hero.png"
                  alt="Studio"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 md:pt-8 space-y-6 text-ink/80 leading-relaxed">
              <p className="font-serif text-2xl md:text-3xl leading-[1.2] text-ink tracking-tight">
                Muse n Aura Designs is an Interior Design Studio founded in 2016, working across Hospitality, Luxury Residential and Commercial interiors.
              </p>
              <p>
                We believe exceptional spaces are created through the perfect balance of creativity, functionality, and flawless execution. Our multidisciplinary team specializes in designing and delivering premium residential, commercial, and hospitality interiors that reflect each client's unique aspirations.
              </p>
              <p>
                By combining innovative design, superior materials, skilled craftsmanship, and disciplined project management, we create environments that inspire, perform, and stand the test of time.
              </p>
              <div className="text-[11px] uppercase tracking-[0.28em] text-muteInk pt-4 leading-relaxed">
                <span className="text-ink">Muse n Aura Designs</span> · Featured In ·{" "}
                <a
                  href="https://hospitality.economictimes.indiatimes.com/news/hotels/wonderla-holidays-unveils-terrea-by-wonderla-bengalurus-newest-nature-themed-urban-resort/130273061"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="editorial-link text-ink"
                  data-testid="feature-et-hospitality"
                >ET Hospitality World</a>{" "}·{" "}
                <a
                  href="https://hospitalitybizindia.com/news-track/wonderla-launches-terrea-nature-themed-urban-resort-in-bengaluru/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="editorial-link text-ink"
                  data-testid="feature-hospitality-biz"
                >Hospitality Biz India</a>{" "}·{" "}
                <a
                  href="https://www.traveltrendstoday.in/wonderla-holidays-unveils-84-key-terrea-by-wonderla-in-bengaluru"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="editorial-link text-ink"
                  data-testid="feature-travel-trends"
                >Travel Trends Today</a>{" "}·{" "}
                <a
                  href="https://safariindia.com/wonderla-launches-terrea-bengaluru/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="editorial-link text-ink"
                  data-testid="feature-safari-india"
                >Safari India</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee items={["Megha Bhardwaj", "&", "Anupam Gulati", "·", "Muse n Aura"]} speed={18} />

      <section className="py-24 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-muteInk mb-16">— People</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 max-w-3xl">
            {TEAM.map((t, i) => (
              <motion.button
                type="button"
                key={t.name}
                onClick={() => setSelected(t)}
                data-testid={`team-card-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className="text-left hoverable group"
              >
                <div className="clip-frame w-full h-[380px] md:h-[520px] bg-stone2 relative overflow-hidden">
                  {t.image && (
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  )}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-ink/60 via-transparent to-transparent flex items-end p-6">
                    <span className="text-bone text-[11px] uppercase tracking-[0.28em]">Read bio →</span>
                  </div>
                </div>
                <h4 className="font-serif text-2xl mt-6 text-ink">{t.name}</h4>
                <p className="text-[11px] uppercase tracking-[0.24em] text-muteInk mt-2">{t.role}</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent
          className="max-w-4xl w-[calc(100%-2rem)] max-h-[90vh] md:max-h-[85vh] overflow-y-auto bg-bone border-borderWarm p-0"
          data-lenis-prevent
        >
          {selected && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="h-[240px] md:h-[560px] overflow-hidden">
                <img src={selected.image} alt={selected.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-10 flex flex-col">
                <DialogHeader className="text-left space-y-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-clay">{selected.role}</p>
                  <DialogTitle className="font-serif text-4xl md:text-5xl text-ink leading-[0.98] tracking-tight">
                    {selected.name}
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-8 border-t border-borderWarm pt-6 text-ink/80 leading-relaxed text-[15px]">
                  {selected.bio}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
